import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/AddUnit.css"; // Reusing the existing stylesheet

const AddDepartmentDiscount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState("");
  const [discount, setDiscount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [discountId, setDiscountId] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL || "";

  // Fetch departments to populate the dropdown menu
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetch(`${API_URL}department-list.php`);
        const data = await response.json();
        // Assumes the department list API returns { "data": [...] }
        if (Array.isArray(data.data)) {
          setDepartments(data.data);
        } else {
          showToast("Could not fetch departments list", "error");
        }
      } catch (error) {
        showToast("Network error while fetching departments", "error");
      }
    };
    fetchDepartments();
  }, [API_URL]);

  useEffect(() => {
    // Check if we're in edit mode
    if (location.state && location.state.discount && location.state.isEditing) {
      const { discount } = location.state;
      setDepartmentId(discount.dept_name_id || discount.dept_id || "");
      setDiscount(discount.discount || "");
      setDiscountId(discount.id);
      setIsEditing(true);
    } else {
      // Reset to add mode if no edit state
      setDepartmentId("");
      setDiscount("");
      setDiscountId(null);
      setIsEditing(false);
    }
  }, [location.state]);

  // Cleanup effect to reset state when component unmounts or location changes
  useEffect(() => {
    return () => {
      setDepartmentId("");
      setDiscount("");
      setDiscountId(null);
      setIsEditing(false);
    };
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!departmentId) {
      showToast("Please select a department", "error");
      return;
    }
    if (!discount || parseFloat(discount) <= 0) {
      showToast("Please enter a valid discount percentage", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const endpoint = isEditing ? "edit-dept-discount.php" : "add-dept-discount.php";
      const body = isEditing 
        ? { 
            id: discountId, 
            dept_name_id: Number(departmentId), 
            discount: parseFloat(discount) 
          }
        : {
            dept_name_id: Number(departmentId),
            discount: parseFloat(discount),
          };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.message && data.message.includes("successfully")) {
        const action = isEditing ? "updated" : "added";
        showToast(`Department discount ${action} successfully! 🎉`, "success");
        setTimeout(() => {
          navigate("/department-discount-list");
        }, 1500);
      } else if (data.status === "success") {
        // Fallback for APIs that use status field
        const action = isEditing ? "updated" : "added";
        showToast(`Department discount ${action} successfully! 🎉`, "success");
        setTimeout(() => {
          navigate("/department-discount-list");
        }, 1500);
      } else {
        showToast(data.message || `Failed to ${isEditing ? 'update' : 'add'} discount`, "error");
      }
    } catch (error) {
      showToast(`Network error: ${error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-category-content">
      <div className="page-header" style={{ display: "block" }}>
        <h1>{isEditing ? "Edit Department Discount" : "Add Department Discount"}</h1>
        <p>{isEditing ? "Update department discount information" : "Assign a special discount to a department"}</p>
      </div>
      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-unit-form">
          <div className="form-group">
            <label htmlFor="departmentId">Department Name</label>
            <select
              id="departmentId"
              name="departmentId"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              disabled={isSubmitting || departments.length === 0}
              required
            >
              <option value="">Select a Department</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.dept_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="discount">Discount (%)</label>
            <input
              type="number"
              id="discount"
              name="discount"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="e.g., 12.5"
              step="0.01"
              min="0"
              required
              disabled={isSubmitting}
            />
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting 
              ? (isEditing ? "Updating Discount..." : "Adding Discount...") 
              : (isEditing ? "Update Discount" : "Add Discount")
            }
          </button>
        </form>
      </div>
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDepartmentDiscount;
